import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlacesAutocomplete from 'react-places-autocomplete'
import PlaceSelect from './PlaceSelect'
import { PlaceParser } from './PlaceParser'
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete'
import propTypes from 'prop-types'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

const PlacePicker = ({ onChange, readOnly = false, initialValue }) => {
  const { t } = useTranslation()
  const [addressData, setAddressData] = useState(initialValue)
  const [defaultPosition, setDefaultPosition] = useState({
    latitude: null,
    longitude: null,
    address: t('Place_picker_select_place_by_dragging_mark'),
  })

  const placesAutocompleteOnChange = (addressData) => {
    setAddressData(addressData)
  }

  const placeSelectOnChange = async (position) => {
    setAddressData(position.address)
    let places = await geocodeByAddress(position.address)
    let place = PlaceParser(places[0])

    let address = {
      city: place.city,
      zip: place.zip_code,
      street_name: place.street_name,
      street_number: place.street_number,
      country: place.country_short,
      latLng: { lat: position.lat, lng: position.lng },
    }
    setDefaultPosition({
      address: position.address,
      lat: position.lat,
      lng: position.lng,
    })
    onChange(address)
  }

  const placesAutocompleteOnSelect = async (data, data1) => {
    setAddressData(data1)

    let address = {
      city: '',
      zip: '',
      street_name: '',
      street_number: '',
      country: '',
      latLng: { lat: 0, lng: 0 },
    }

    try {
      let results = await geocodeByAddress(data1)
      let latLng = await getLatLng(results[0])
      let place = PlaceParser(results[0])

      address = {
        city: place.city,
        zip: place.zip_code,
        street_name: place.street_name,
        street_number: place.street_number,
        country: place.country_short,
        latLng: { lat: latLng.lat, lng: latLng.lng },
      }
      setDefaultPosition({
        address: data1,
        lat: latLng.lat,
        lng: latLng.lng,
      })

      onChange(address)
    } catch (error) {
      console.error('Error', error)
    }
  }

  return (
    <div className="space-y-2">
      <Label>{t('Place_picker_address_autocomplete_label')}</Label>
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <PlacesAutocomplete
            value={addressData}
            onChange={placesAutocompleteOnChange}
            onSelect={(e) => placesAutocompleteOnSelect(addressData, e)}
            shouldFetchSuggestions={addressData.length > 2}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <>
                <Input
                  disabled={readOnly}
                  id="address_autocomplete"
                  {...getInputProps({
                    name: 'address_autocomplete',
                    placeholder: t('Place_picker_address_autocomplete_placeholder'),
                  })}
                />
                {suggestions.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                    {loading && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">...</div>
                    )}
                    {suggestions.map((suggestion) => (
                      <div
                        {...getSuggestionItemProps(suggestion)}
                        className={cn(
                          'cursor-pointer px-3 py-2 text-sm',
                          suggestion.active ? 'bg-accent' : 'hover:bg-accent',
                        )}
                        key={suggestion.placeId}
                      >
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </PlacesAutocomplete>
        </div>
        <PlaceSelect
          readOnly={readOnly}
          defaultChosenPosition={defaultPosition}
          addrText={addressData}
          onChange={placeSelectOnChange}
        />
      </div>
    </div>
  )
}

PlacePicker.propTypes = {
  initialValue: propTypes.string
}

PlacePicker.defaultProps = {
  initialValue: ''
}

export default PlacePicker
